import React, { useMemo } from 'react'
import { AgGridReact } from '@ag-grid-community/react'
import type { ColDef, ICellRendererParams } from '@ag-grid-community/core'
import { Spin, Tag } from '@surecompanies/core_components'
import Tooltip from 'antd/es/tooltip'
import dayjs from 'dayjs'
import { relativeTimeString } from 'enrollment-platform/utils/relative-time-string'
import { PlanOverrideAction } from '../enums/plan-override-action.enum'
import type { PlanOverrideHistoryEntryDto } from '../api/dto/plan-override-history.dto'
import type {
  PlanBenefitOverrideRuleStatus,
  PlanBenefitOverrideRuleTemplateKey,
} from '../pages/PlanBenefitOverrides/enums/plan-benefit-override-rule.enum'
import { useOverrideHistory } from '../hooks/useOverrideHistory'
import { useGetOverrideRuleTemplates } from '../pages/PlanBenefitOverrides/hooks/useGetOverrideRuleTemplates'
import { PLAN_BENEFIT_OVERRIDE_RULE_PATTERN_FORMATTER } from '../pages/PlanBenefitOverrides/components/rule-templates'
import PlanOverridesPagination from './PlanOverridesPagination'
import styles from './PlanOverridesComponents.module.scss'

interface Props {
  ruleId: string
  ruleStatus?: PlanBenefitOverrideRuleStatus
  ruleTemplate?: PlanBenefitOverrideRuleTemplateKey
  ruleBase?: string
  displayVariant?: 'default' | 'serviceAreaLike' | 'planAndRate'
  /** Override the "What to change" label instead of deriving from ruleTemplate */
  staticTemplateLabel?: string
  /** Override the params → "Change to" formatter instead of the benefit formatter */
  paramsFormatter?: (params: Record<string, unknown>) => string
}

const ACTION_LABELS: Record<PlanOverrideAction, string> = {
  [PlanOverrideAction.OVERRIDE_CREATED]: 'Created',
  [PlanOverrideAction.OVERRIDE_UPDATED]: 'Updated',
  [PlanOverrideAction.OVERRIDE_STATUS_CHANGED]: 'Status changed',
  [PlanOverrideAction.OVERRIDE_DOCUMENT_ADDED]: 'Document added',
  [PlanOverrideAction.OVERRIDE_DOCUMENT_REMOVED]: 'Document removed',
  [PlanOverrideAction.OVERRIDE_DELETED]: 'Deleted',
}

const ACTION_TAG_COLOR: Record<PlanOverrideAction, string> = {
  [PlanOverrideAction.OVERRIDE_CREATED]: 'green',
  [PlanOverrideAction.OVERRIDE_UPDATED]: 'blue',
  [PlanOverrideAction.OVERRIDE_STATUS_CHANGED]: 'orange',
  [PlanOverrideAction.OVERRIDE_DOCUMENT_ADDED]: 'cyan',
  [PlanOverrideAction.OVERRIDE_DOCUMENT_REMOVED]: 'volcano',
  [PlanOverrideAction.OVERRIDE_DELETED]: 'red',
}

const STATUS_TAG_COLOR: Record<PlanBenefitOverrideRuleStatus, string> = {
  DRAFT: 'gold',
  APPROVED: 'green',
  REJECTED: 'red',
}

function ActionCellRenderer({
  data,
}: ICellRendererParams<PlanOverrideHistoryEntryDto>) {
  if (!data) return null
  const label = ACTION_LABELS[data.action] ?? data.action
  const color = ACTION_TAG_COLOR[data.action] ?? 'default'
  return <Tag color={color}>{label}</Tag>
}

function ActionTextCellRenderer({
  data,
}: ICellRendererParams<PlanOverrideHistoryEntryDto>) {
  if (!data) return null
  const label = ACTION_LABELS[data.action] ?? data.action
  return <span>{label}</span>
}

interface StatusCellRendererParams
  extends ICellRendererParams<PlanOverrideHistoryEntryDto> {
  ruleStatus?: PlanBenefitOverrideRuleStatus
}

function StatusCellRenderer({ data, ruleStatus }: StatusCellRendererParams) {
  if (!data) return null

  if (
    data.action === PlanOverrideAction.OVERRIDE_STATUS_CHANGED &&
    data.statusChange
  ) {
    const { from, to } = data.statusChange
    if (from !== to) {
      return (
        <span className={styles.historyStatusChange}>
          <Tag color={STATUS_TAG_COLOR[from] ?? 'default'}>{from}</Tag>
          {'→'}
          <Tag color={STATUS_TAG_COLOR[to] ?? 'default'}>{to}</Tag>
        </span>
      )
    }
    return <Tag color={STATUS_TAG_COLOR[to] ?? 'default'}>{to}</Tag>
  }

  const status = data.status ?? ruleStatus
  if (status) {
    return <Tag color={STATUS_TAG_COLOR[status] ?? 'default'}>{status}</Tag>
  }

  return <span className={styles.historyEmptyCell}>—</span>
}

interface TemplateCellRendererParams
  extends ICellRendererParams<PlanOverrideHistoryEntryDto> {
  templateLabel?: string
}

function TemplateCellRenderer({ templateLabel }: TemplateCellRendererParams) {
  if (!templateLabel) return <span className={styles.historyEmptyCell}>—</span>
  return (
    <Tooltip title={templateLabel} placement="topLeft">
      <span className={styles.historyCellTruncated}>{templateLabel}</span>
    </Tooltip>
  )
}

interface ChangeToCellParams
  extends ICellRendererParams<PlanOverrideHistoryEntryDto> {
  paramsFormatter?: (params: Record<string, unknown>) => string
}

function ChangeToCell({ data, paramsFormatter }: ChangeToCellParams) {
  if (!data) return null

  let template: PlanBenefitOverrideRuleTemplateKey | null = null
  let params: Record<string, unknown> | null = null

  if (data.action === PlanOverrideAction.OVERRIDE_CREATED && data.snapshot) {
    template =
      (data.snapshot.template as PlanBenefitOverrideRuleTemplateKey) ?? null
    params = (data.snapshot.params as Record<string, unknown>) ?? null
  } else if (
    data.action === PlanOverrideAction.OVERRIDE_DELETED &&
    data.finalSnapshot
  ) {
    template =
      (data.finalSnapshot.template as PlanBenefitOverrideRuleTemplateKey) ??
      null
    params = (data.finalSnapshot.params as Record<string, unknown>) ?? null
  } else {
    template = data.ruleTemplate
    params = data.params
  }

  if (!params) return <span className={styles.historyEmptyCell}>—</span>

  try {
    let formatted: string | undefined
    if (paramsFormatter) {
      formatted = paramsFormatter(params)
    } else if (template) {
      const formatter = PLAN_BENEFIT_OVERRIDE_RULE_PATTERN_FORMATTER[template]
      if (!formatter) return <span className={styles.historyEmptyCell}>—</span>
      formatted = formatter(params)
    }
    if (!formatted) return <span className={styles.historyEmptyCell}>—</span>
    return (
      <Tooltip title={formatted} placement="topLeft">
        <span className={styles.historyCellTruncated}>{formatted}</span>
      </Tooltip>
    )
  } catch {
    return <span className={styles.historyEmptyCell}>—</span>
  }
}

function ActorCellRenderer({
  data,
}: ICellRendererParams<PlanOverrideHistoryEntryDto>) {
  if (!data) return null
  return (
    <Tooltip title={data.actorName} placement="topLeft">
      <span className={`${styles.historyActor} ${styles.historyCellTruncated}`}>
        {data.actorName}
      </span>
    </Tooltip>
  )
}

const DEFAULT_COL_DEF: ColDef = {
  sortable: false,
  filter: false,
  resizable: true,
  suppressMovable: true,
}

const getRowId = ({ data }: { data: PlanOverrideHistoryEntryDto }) => data.id

function getMatchString(
  data: PlanOverrideHistoryEntryDto | undefined,
  key: keyof PlanOverrideHistoryEntryDto
): string {
  if (!data) return '—'
  const raw = data[key]
  return typeof raw === 'string' && raw.trim() ? raw : '—'
}

function getMatchNumber(
  data: PlanOverrideHistoryEntryDto | undefined,
  key: keyof PlanOverrideHistoryEntryDto
): string {
  if (!data) return '—'
  const raw = data[key]
  return typeof raw === 'number' ? String(raw) : '—'
}

function resolveHistoryParams(
  data: PlanOverrideHistoryEntryDto | undefined
): Record<string, unknown> | null {
  if (!data) return null
  if (data.action === PlanOverrideAction.OVERRIDE_CREATED && data.snapshot) {
    return (data.snapshot.params as Record<string, unknown>) ?? null
  }
  if (
    data.action === PlanOverrideAction.OVERRIDE_DELETED &&
    data.finalSnapshot
  ) {
    return (data.finalSnapshot.params as Record<string, unknown>) ?? null
  }
  return data.params
}

function getParamString(
  data: PlanOverrideHistoryEntryDto | undefined,
  key: string
): string {
  const raw = resolveHistoryParams(data)?.[key]
  return typeof raw === 'string' && raw.trim() ? raw : '—'
}

function getParamBoolean(
  data: PlanOverrideHistoryEntryDto | undefined,
  key: string
): boolean | undefined {
  const raw = resolveHistoryParams(data)?.[key]
  return typeof raw === 'boolean' ? raw : undefined
}

function getParamNumberValue(
  data: PlanOverrideHistoryEntryDto | undefined,
  key: string
): string {
  const raw = resolveHistoryParams(data)?.[key]
  return typeof raw === 'number' ? raw.toFixed(3) : '—'
}

function getParamBoolText(
  data: PlanOverrideHistoryEntryDto | undefined,
  key: string
): string {
  const raw = resolveHistoryParams(data)?.[key]
  if (typeof raw === 'boolean') return raw ? '✓' : '✗'
  return '—'
}

function getParamRateAdjustment(
  data: PlanOverrideHistoryEntryDto | undefined,
  raKey: string
): string {
  const ra = resolveHistoryParams(data)?.rateAdjustments
  if (!ra || typeof ra !== 'object') return '—'
  const val = (ra as Record<string, unknown>)[raKey]
  return typeof val === 'number' ? val.toFixed(3) : '—'
}

const PLAN_AND_RATE_RA_KEYS = Array.from(
  { length: 67 },
  (_, i) => `RA${String(i + 1).padStart(2, '0')}`
)

export default function OverrideHistoryTimeline({
  ruleId,
  ruleStatus,
  ruleTemplate,
  ruleBase,
  displayVariant = 'default',
  staticTemplateLabel,
  paramsFormatter,
}: Props) {
  const { entries, isLoading, total, page, limit, goToPage, setLimit } =
    useOverrideHistory(ruleId, ruleBase)
  const { templatesByKey } = useGetOverrideRuleTemplates()

  const templateLabel =
    staticTemplateLabel ??
    (ruleTemplate
      ? (templatesByKey[ruleTemplate]?.label ?? ruleTemplate)
      : undefined)

  const isServiceAreaLike = displayVariant === 'serviceAreaLike'
  const isPlanAndRate = displayVariant === 'planAndRate'

  const columnDefs = useMemo<ColDef<PlanOverrideHistoryEntryDto>[]>(
    () =>
      isServiceAreaLike
        ? [
            {
              colId: 'action',
              headerName: 'Action',
              field: 'action',
              cellRenderer: ActionTextCellRenderer,
              minWidth: 130,
              pinned: 'left',
              lockPinned: true,
              suppressMovable: true,
            },
            {
              colId: 'updated',
              headerName: 'Updated',
              field: 'occurredAt',
              valueFormatter: (p) =>
                p.value ? relativeTimeString(p.value as string, 30) : '—',
              minWidth: 160,
              sort: 'desc',
              sortable: true,
              pinned: 'left',
              lockPinned: true,
              suppressMovable: true,
            },
            {
              colId: 'year',
              headerName: 'Year',
              valueGetter: (p) => getMatchNumber(p.data, 'matchYear'),
              minWidth: 80,
              sortable: true,
            },
            {
              colId: 'state',
              headerName: 'State',
              valueGetter: (p) => getMatchString(p.data, 'matchState'),
              minWidth: 80,
              sortable: true,
            },
            {
              colId: 'issuerId',
              headerName: 'Issuer ID',
              valueGetter: (p) => getMatchString(p.data, 'matchIssuerId'),
              minWidth: 100,
              sortable: true,
            },
            {
              colId: 'hiosPlanId',
              headerName: 'HIOS Plan ID',
              valueGetter: (p) => getMatchString(p.data, 'matchHiosPlanId'),
              minWidth: 130,
              sortable: true,
            },
            {
              colId: 'serviceAreaId',
              headerName: 'Service Area ID',
              valueGetter: (p) => getMatchString(p.data, 'matchServiceAreaId'),
              minWidth: 120,
              sortable: true,
            },
            {
              colId: 'countyCode',
              headerName: 'County Code',
              valueGetter: (p) => getMatchString(p.data, 'matchCountyCode'),
              minWidth: 100,
              sortable: true,
            },
            {
              colId: 'zipcode',
              headerName: 'Zipcode',
              valueGetter: (p) => getMatchString(p.data, 'matchZipcode'),
              minWidth: 90,
              sortable: true,
            },
            {
              colId: 'sourceName',
              headerName: 'Source Name',
              valueGetter: (p) => getParamString(p.data, 'sourceName'),
              minWidth: 120,
            },
            {
              colId: 'serviceAreaName',
              headerName: 'Service Area Name',
              valueGetter: (p) => getParamString(p.data, 'serviceAreaName'),
              minWidth: 200,
            },
            {
              colId: 'isActive',
              headerName: 'Is Active',
              valueGetter: (p) => {
                const isActive = getParamBoolean(p.data, 'isActive')
                if (typeof isActive === 'boolean') {
                  return isActive ? 'Yes' : 'No'
                }
                return p.data?.status === 'APPROVED'
                  ? 'Yes'
                  : p.data?.status === 'DRAFT' || p.data?.status === 'REJECTED'
                    ? 'No'
                    : '—'
              },
              minWidth: 80,
            },
            {
              colId: 'updatedBy',
              headerName: 'Updated by',
              field: 'actorName',
              minWidth: 220,
            },
          ]
        : isPlanAndRate
          ? [
              {
                colId: 'action',
                headerName: 'Action',
                field: 'action',
                cellRenderer: ActionCellRenderer,
                minWidth: 130,
              },
              {
                colId: 'updated',
                headerName: 'Updated',
                field: 'occurredAt',
                valueFormatter: (p) =>
                  p.value ? relativeTimeString(p.value as string, 30) : '—',
                minWidth: 160,
                sort: 'desc',
                sortable: true,
              },
              {
                colId: 'status',
                headerName: 'Status',
                cellRenderer: StatusCellRenderer,
                cellRendererParams: { ruleStatus },
                width: 210,
              },
              {
                colId: 'planName',
                headerName: 'Plan Name',
                valueGetter: (p) => getParamString(p.data, 'planName'),
                minWidth: 160,
              },
              {
                colId: 'carrier',
                headerName: 'Carrier',
                valueGetter: (p) => getParamString(p.data, 'carrier'),
                minWidth: 130,
              },
              {
                colId: 'householdName',
                headerName: 'Carrier Friendly Name',
                valueGetter: (p) => getParamString(p.data, 'householdName'),
                minWidth: 160,
              },
              {
                colId: 'planType',
                headerName: 'Plan Type',
                valueGetter: (p) => getParamString(p.data, 'planType'),
                minWidth: 100,
              },
              {
                colId: 'level',
                headerName: 'Metal Level',
                valueGetter: (p) => getParamString(p.data, 'level'),
                minWidth: 110,
              },
              {
                colId: 'serviceAreaId',
                headerName: 'Service Area ID',
                valueGetter: (p) => getParamString(p.data, 'serviceAreaId'),
                minWidth: 130,
              },
              {
                colId: 'isForSale',
                headerName: 'For Sale',
                valueGetter: (p) => getParamBoolText(p.data, 'isForSale'),
                minWidth: 90,
              },
              {
                colId: 'hsa',
                headerName: 'HSA',
                valueGetter: (p) => getParamBoolText(p.data, 'hsa'),
                minWidth: 70,
              },
              {
                colId: 'networkName',
                headerName: 'Network',
                valueGetter: (p) => getParamString(p.data, 'networkName'),
                minWidth: 130,
              },
              {
                colId: 'onExchange',
                headerName: 'On Exchange',
                valueGetter: (p) => getParamBoolText(p.data, 'onExchange'),
                minWidth: 110,
              },
              {
                colId: 'offExchange',
                headerName: 'Off Exchange',
                valueGetter: (p) => getParamBoolText(p.data, 'offExchange'),
                minWidth: 110,
              },
              {
                colId: 'planStatus',
                headerName: 'Plan Status',
                valueGetter: (p) => getParamString(p.data, 'planStatus'),
                minWidth: 110,
              },
              {
                colId: 'crosswalkPlanId',
                headerName: 'Crosswalk Plan ID',
                valueGetter: (p) => getParamString(p.data, 'crosswalkPlanId'),
                minWidth: 150,
              },
              {
                colId: 'baseRate',
                headerName: 'Base Rate',
                valueGetter: (p) => getParamNumberValue(p.data, 'baseRate'),
                minWidth: 100,
              },
              {
                colId: 'planFee',
                headerName: 'Plan Fee',
                valueGetter: (p) => getParamNumberValue(p.data, 'planFee'),
                minWidth: 100,
              },
              {
                colId: 'proofOfResidenceRequired',
                headerName: 'Proof of Residence',
                valueGetter: (p) =>
                  getParamBoolText(p.data, 'proofOfResidenceRequired'),
                minWidth: 150,
              },
              ...PLAN_AND_RATE_RA_KEYS.map((raKey) => ({
                colId: raKey,
                headerName: raKey,
                valueGetter: (p: { data?: PlanOverrideHistoryEntryDto }) =>
                  getParamRateAdjustment(p.data, raKey),
                minWidth: 80,
              })),
              {
                colId: 'updatedBy',
                headerName: 'Updated by',
                field: 'actorName',
                minWidth: 220,
              },
            ]
          : [
              {
                headerName: 'Date / Time',
                field: 'occurredAt',
                valueFormatter: (p) =>
                  p.value
                    ? dayjs(p.value as string).format('DD MMM YYYY HH:mm')
                    : '—',
                width: 155,
              },
              {
                headerName: 'Action',
                field: 'action',
                cellRenderer: ActionCellRenderer,
                width: 140,
              },
              {
                headerName: 'What to change',
                colId: 'whatToChange',
                cellRenderer: TemplateCellRenderer,
                cellRendererParams: { templateLabel },
                width: 170,
              },
              {
                headerName: 'Change to',
                field: 'finalSnapshot',
                cellRenderer: ChangeToCell,
                cellRendererParams: { paramsFormatter },
                flex: 1,
                minWidth: 180,
              },
              {
                headerName: 'Status',
                field: 'statusChange',
                cellRenderer: StatusCellRenderer,
                cellRendererParams: { ruleStatus },
                width: 210,
              },
              {
                headerName: 'Actor',
                field: 'actorName',
                cellRenderer: ActorCellRenderer,
                autoHeight: true,
                flex: 1,
                minWidth: 180,
              },
            ],
    [
      isServiceAreaLike,
      isPlanAndRate,
      paramsFormatter,
      ruleStatus,
      templateLabel,
    ]
  )

  if (isLoading) {
    return (
      <div className={styles.historyLoadingWrapper}>
        <Spin size="large" />
      </div>
    )
  }

  return (
    <div>
      <div className="ag-theme-alpine grid">
        <AgGridReact
          getRowId={getRowId}
          rowData={entries}
          columnDefs={columnDefs}
          defaultColDef={DEFAULT_COL_DEF}
          domLayout="autoHeight"
          suppressRowClickSelection
          suppressColumnVirtualisation={isPlanAndRate}
          overlayNoRowsTemplate="No history yet"
        />
      </div>
      <PlanOverridesPagination
        page={page}
        limit={limit}
        total={total}
        onPageChange={goToPage}
        onLimitChange={setLimit}
      />
    </div>
  )
}
